import { useEffect, useId, useRef, useState, type MouseEvent } from 'react'
import { SidebarFilterBar } from '@/components/layout/sidebar/SidebarFilterBar'
import {
  Mic, Bot, Zap, Building2, Mail, Archive, MessageSquare, MoreVertical,
  Search, X, AlignJustify, List, Clock, Pin, Folder, RefreshCw,
  RotateCcw, CheckCheck, Plug,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
} from '@/components/ui/dropdown-menu'
import { IconButton } from '@/components/ui/icon-button'
import type { Thread, Kind, KindFilter, InboxWorkspace, Status } from './types'
import { SidebarListRow } from '@/components/layout/sidebar/SidebarListRow'
import { SidebarEmptyState } from '@/components/layout/sidebar/SidebarEmptyState'
import { testIds } from '@/lib/test-ids'
import { useChatSurfaceRegistryOptional } from '@/hooks/use-chat-surface-registry'
import { openInboxThreadInChat } from '@/lib/open-inbox-thread-in-chat'
import { copyInboxThreadLink, dispatchInboxThreadAddToChat } from '@/lib/inbox-thread-share'
import { useWorkspace } from '@/providers/workspace-provider'
import { useNeedsPassword } from '@/hooks/use-needs-password'
import { InboxPasswordSetupBanner } from './InboxPasswordSetupBanner'
import { isInboxThreadVisible, resolveThreadWorkspace } from './mappers'
import {
  QUIET_OVERFLOW_MENU_COLLISION_PADDING,
  QUIET_OVERFLOW_MENU_CONTENT_CLASS,
} from '@/lib/menu-chrome'
import {
  CLOSE_TRANSIENT_MENUS_EVENT,
  closeTransientMenus,
  type CloseTransientMenusDetail,
} from '@/lib/transient-menus'
import {
  getInboxThreadContextMenuEntries,
  getInboxThreadMenuActions,
  INBOX_SNOOZE_OPTIONS,
  type InboxThreadMenuSurface,
} from './inbox-thread-menu'

// ---------------------------------------------------------------------------
// Kind config
// ---------------------------------------------------------------------------

const KIND_CONFIG: Record<Kind, { label: string; icon: React.ElementType; color: string }> = {
  voice:      { label: 'Voice',       icon: Mic,           color: 'text-purple-400' },
  email:      { label: 'Email',       icon: Mail,          color: 'text-orange-400' },
  agent:      { label: 'Agent',       icon: Bot,           color: 'text-blue-400' },
  suggestion: { label: 'Suggestions', icon: Zap,           color: 'text-yellow-400' },
  invite:     { label: 'Invite',      icon: Zap,           color: 'text-amber-400' },
  chat:       { label: 'Chats',       icon: MessageSquare, color: 'text-sky-400' },
  flow:       { label: 'Flows',       icon: Zap,           color: 'text-green-400' },
  extension:  { label: 'Extensions',  icon: Building2,     color: 'text-teal-400' },
}

// ---------------------------------------------------------------------------
// KindTabs
// ---------------------------------------------------------------------------

function KindTabs({
  filter,
  onChange,
  threads,
  activeWorkspaceIds,
}: {
  filter: KindFilter
  onChange: (f: KindFilter) => void
  threads: Thread[]
  activeWorkspaceIds: string[]
}) {
  const scopedThreads = threads.filter(t => isInboxThreadVisible(t, activeWorkspaceIds))

  const counts: Partial<Record<Kind, number>> = {}
  for (const t of scopedThreads) {
    if (t.status === 'snoozed' || t.status === 'archived') continue
    if (t.status === 'unread') counts[t.kind] = (counts[t.kind] ?? 0) + 1
  }
  const totalUnread = Object.values(counts).reduce((a, b) => a + b, 0)
  const snoozedCount = scopedThreads.filter(t => t.status === 'snoozed').length
  const archivedCount = threads.filter(t => t.status === 'archived').length

  type TabDef = { id: KindFilter; label: string; icon?: React.ElementType; count?: number }
  const tabs: TabDef[] = [
    { id: 'all', label: 'All', count: totalUnread },
    ...Object.entries(KIND_CONFIG)
      .filter(([k]) => scopedThreads.some(t => t.kind === (k as Kind) && t.status !== 'archived'))
      .map(([k, meta]) => ({ id: k as KindFilter, label: meta.label, icon: meta.icon, count: counts[k as Kind] })),
    { id: 'snoozed',  label: 'Snoozed', icon: Clock,   count: snoozedCount },
    { id: 'archived', label: 'Archive',  icon: Archive, count: archivedCount },
  ]

  return (
    <div className="flex items-center gap-1 overflow-x-auto" style={{ scrollbarWidth: 'none' }}>
      {tabs.map((tab, i) => {
        const Icon = tab.icon
        const active = filter === tab.id
        return (
          <button
            key={`${tab.id}-${i}`}
            onClick={() => onChange(tab.id)}
            className={cn(
              'flex items-center gap-1 px-2 py-1 rounded text-xs font-medium whitespace-nowrap transition-colors shrink-0 border',
              active
                ? 'bg-primary/10 text-primary border-primary/20'
                : 'text-muted-foreground hover:bg-muted hover:text-foreground border-transparent'
            )}
          >
            {Icon && <Icon className="h-3 w-3 shrink-0" />}
            <span>{tab.label}</span>
            {tab.count != null && tab.count > 0 && (
              <span className={cn(
                'text-[9px] font-bold rounded-full px-1 leading-4 min-w-[13px] text-center',
                active ? 'bg-primary text-primary-foreground' : 'bg-muted-foreground/20 text-muted-foreground'
              )}>
                {tab.count}
              </span>
            )}
          </button>
        )
      })}
    </div>
  )
}

// ---------------------------------------------------------------------------
// ThreadActionsMenu (overflow) — same item source as right-click (ADR-0020)
// ---------------------------------------------------------------------------

export function ThreadActionsMenu({
  threadId,
  threadStatus = 'read',
  linkedConversationId,
  onSnooze,
  onArchive,
  onMarkAsUnread,
  onMarkAsRead,
  onOpenInChat,
  onOpenInNewTab,
  onPreview,
  threadTitle,
  surface = 'reader',
  compact = false,
}: {
  threadId: string
  threadTitle?: string
  threadStatus?: Status
  linkedConversationId?: string | null
  onSnooze: (id: string, label: string, hint: string) => void
  onArchive: (id: string) => void
  onMarkAsUnread?: (id: string) => void
  onMarkAsRead?: (id: string) => void
  onOpenInChat?: (id: string) => void
  onOpenInNewTab?: (id: string) => void
  onPreview?: (id: string) => void
  surface?: InboxThreadMenuSurface
  compact?: boolean
}) {
  const registry = useChatSurfaceRegistryOptional()
  const menuSourceId = useId()
  const [open, setOpen] = useState(false)

  useEffect(() => {
    const onExclusiveClose = (event: Event) => {
      const detail = (event as CustomEvent<CloseTransientMenusDetail>).detail
      if (detail?.source === menuSourceId) return
      setOpen(false)
    }
    window.addEventListener(CLOSE_TRANSIENT_MENUS_EVENT, onExclusiveClose)
    return () => {
      window.removeEventListener(CLOSE_TRANSIENT_MENUS_EVENT, onExclusiveClose)
    }
  }, [menuSourceId])

  function handleOpenChat() {
    if (onOpenInChat) {
      onOpenInChat(threadId)
      return
    }
    void openInboxThreadInChat({
      threadId,
      linkedConversationId,
      registry,
      forceAgentPanel: true,
    })
  }

  const actions = getInboxThreadMenuActions({
    status: threadStatus,
    linkedConversationId,
    surface,
    handlers: {
      onOpenInNewTab: onOpenInNewTab ? () => onOpenInNewTab(threadId) : undefined,
      onPreview: onPreview ? () => onPreview(threadId) : undefined,
      onSnooze: (label, hint) => onSnooze(threadId, label, hint),
      onArchive: () => onArchive(threadId),
      onMarkAsUnread: onMarkAsUnread ? () => onMarkAsUnread(threadId) : undefined,
      onMarkAsRead: onMarkAsRead ? () => onMarkAsRead(threadId) : undefined,
      onOpenInChat: (onOpenInChat || linkedConversationId) ? handleOpenChat : undefined,
      onAddToCurrentChat: () => dispatchInboxThreadAddToChat(threadId, threadTitle),
      onCopyLink: () => {
        void copyInboxThreadLink(threadId)
      },
    },
  })

  return (
    <DropdownMenu
      open={open}
      onOpenChange={(next) => {
        if (next) closeTransientMenus(menuSourceId)
        setOpen(next)
      }}
    >
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className={cn(
            'rounded text-muted-foreground/60 hover:bg-muted hover:text-foreground transition-colors',
            compact ? 'p-1' : 'border border-border p-1.5',
          )}
          title="More actions"
          data-testid={testIds.inbox.threadActionsMenu}
        >
          <MoreVertical className={compact ? 'h-3 w-3' : 'h-3.5 w-3.5'} />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className={QUIET_OVERFLOW_MENU_CONTENT_CLASS}
        collisionPadding={QUIET_OVERFLOW_MENU_COLLISION_PADDING}
      >
        {actions.map((action, index) => {
          const prev = actions[index - 1]
          const chatGroup =
            action.id === 'open-in-chat' || action.id === 'add-to-chat' || action.id === 'copy-link'
          const prevInChatGroup =
            prev &&
            (prev.id === 'open-in-chat' || prev.id === 'add-to-chat' || prev.id === 'copy-link')
          const sepBeforeOpen = chatGroup && prev && !prevInChatGroup
          const sepAfterNav =
            (action.id === 'snooze' || action.id === 'archive') &&
            prev &&
            (prev.id === 'open-new-tab' || prev.id === 'preview')

          const Icon = action.icon

          if (action.id === 'snooze' && action.snoozeOptions) {
            return (
              <div key={action.id}>
                {sepAfterNav ? <DropdownMenuSeparator /> : null}
                <DropdownMenuSub>
                  <DropdownMenuSubTrigger>
                    <Clock className="h-4 w-4" />
                    <span>Snooze</span>
                  </DropdownMenuSubTrigger>
                  <DropdownMenuSubContent
                    className={QUIET_OVERFLOW_MENU_CONTENT_CLASS}
                    collisionPadding={QUIET_OVERFLOW_MENU_COLLISION_PADDING}
                  >
                    <DropdownMenuLabel className="px-2 py-1 text-[11px] font-normal text-muted-foreground/70">
                      Snooze until...
                    </DropdownMenuLabel>
                    {INBOX_SNOOZE_OPTIONS.map((opt) => (
                      <DropdownMenuItem
                        key={opt.value}
                        onSelect={() => onSnooze(threadId, opt.label, opt.hint)}
                        className="justify-between gap-4"
                      >
                        <span>{opt.label}</span>
                        <span className="tabular-nums text-xs text-muted-foreground">{opt.hint}</span>
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuSubContent>
                </DropdownMenuSub>
              </div>
            )
          }

          return (
            <div key={action.id}>
              {sepBeforeOpen || sepAfterNav ? <DropdownMenuSeparator /> : null}
              <DropdownMenuItem
                disabled={action.disabled}
                onSelect={() => action.onSelect?.()}
              >
                <Icon className="h-4 w-4" />
                <span>{action.label}</span>
              </DropdownMenuItem>
            </div>
          )
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

// ---------------------------------------------------------------------------
// Banners
// ---------------------------------------------------------------------------

function SnoozeBanner({ label, hint, onUndo }: { label: string; hint: string; onUndo: () => void }) {
  return (
    <div className="flex items-center gap-2 px-3 py-2 bg-amber-500/10 border-b border-amber-500/20 shrink-0">
      <Clock className="h-3.5 w-3.5 text-amber-500 shrink-0" />
      <span className="text-xs text-amber-600 flex-1">Snoozed: {label} ({hint})</span>
      <button onClick={onUndo} className="flex items-center gap-1 text-xs text-amber-600 hover:text-amber-700 transition-colors">
        <RotateCcw className="h-3 w-3" /> Undo
      </button>
    </div>
  )
}

function ArchiveBanner({ onUndo }: { onUndo: () => void }) {
  return (
    <div className="flex items-center gap-2 px-3 py-2 bg-muted/60 border-b border-border shrink-0">
      <Archive className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
      <span className="text-xs text-muted-foreground flex-1">Thread archived</span>
      <button onClick={onUndo} className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors">
        <RotateCcw className="h-3 w-3" /> Undo
      </button>
    </div>
  )
}

// ---------------------------------------------------------------------------
// ThreadRow
// ---------------------------------------------------------------------------

function ThreadRow({
  thread,
  active,
  dense,
  onClick,
  onOpen,
  onArchive,
  onSnooze,
  onMarkAsUnread,
  onMarkAsRead,
  onPreview,
  onOpenInChat,
  workspace,
}: {
  thread: Thread
  active: boolean
  dense: boolean
  onClick: (event: MouseEvent) => void
  /** When set, single-click selects; double-click / context open calls this instead of onClick. */
  onOpen?: (event: MouseEvent) => void
  onArchive: (id: string) => void
  onSnooze: (id: string, label: string, hint: string) => void
  onMarkAsUnread?: (id: string) => void
  onMarkAsRead?: (id: string) => void
  /** Select + ensure preview pane (Continuity: Preview is a menu action). */
  onPreview?: (id: string) => void
  onOpenInChat?: (id: string) => void
  workspace?: InboxWorkspace
}) {
  const isUnread = thread.status === 'unread'
  const isSnoozed = thread.status === 'snoozed'
  const isArchived = thread.status === 'archived'
  const cfg = KIND_CONFIG[thread.kind]
  const Icon = cfg.icon
  const modClickEvent = {
    metaKey: true,
    ctrlKey: false,
    preventDefault: () => {},
    stopPropagation: () => {},
  } as MouseEvent

  const openHandler = onOpen ?? onClick

  const menuHandlers = {
    onOpenInNewTab: () => openHandler(modClickEvent),
    onPreview: onPreview
      ? () => onPreview(thread.id)
      : () => onClick(modClickEvent),
    onSnooze: (label: string, hint: string) => onSnooze(thread.id, label, hint),
    onArchive: () => onArchive(thread.id),
    onMarkAsUnread: onMarkAsUnread ? () => onMarkAsUnread(thread.id) : undefined,
    onMarkAsRead: onMarkAsRead ? () => onMarkAsRead(thread.id) : undefined,
    onOpenInChat: onOpenInChat
      ? () => onOpenInChat(thread.id)
      : thread.linkedConversationId
        ? () => {
            void openInboxThreadInChat({
              threadId: thread.id,
              linkedConversationId: thread.linkedConversationId,
              forceAgentPanel: true,
            })
          }
        : undefined,
    onAddToCurrentChat: () => dispatchInboxThreadAddToChat(thread.id, thread.title),
    onCopyLink: () => {
      void copyInboxThreadLink(thread.id)
    },
  }

  const contextMenuEntries = getInboxThreadContextMenuEntries({
    status: thread.status,
    linkedConversationId: thread.linkedConversationId,
    surface: 'list',
    handlers: menuHandlers,
  })

  return (
    <SidebarListRow
      state={{ active }}
      workspaceColor={workspace?.color}
      workspaceName={workspace?.label}
      showWorkspaceDot={false}
      dense={dense}
      onActivate={(e) => onClick(e as unknown as MouseEvent)}
      onDoubleClick={(e) => {
        e.preventDefault()
        openHandler(modClickEvent)
        window.dispatchEvent(
          new CustomEvent('pin-tab-by-id', { detail: { tabId: `inbox-thread-${thread.id}` } }),
        )
      }}
      contextMenuEntries={contextMenuEntries}
      contextMenuTestId={testIds.inbox.contextMenu}
      className={cn('select-none', isArchived && 'opacity-60')}
      hoverActions={
        !isArchived ? (
          <div onClick={(e) => e.stopPropagation()}>
            <ThreadActionsMenu
              threadId={thread.id}
              threadTitle={thread.title}
              threadStatus={thread.status}
              linkedConversationId={thread.linkedConversationId ?? null}
              surface="list"
              onSnooze={onSnooze}
              onArchive={onArchive}
              onMarkAsUnread={onMarkAsUnread}
              onMarkAsRead={onMarkAsRead}
              onOpenInChat={onOpenInChat}
              onOpenInNewTab={() => {
                openHandler(modClickEvent)
              }}
              onPreview={
                onPreview
                  ? (id) => onPreview(id)
                  : () => {
                      onClick(modClickEvent)
                    }
              }
              compact
            />
          </div>
        ) : undefined
      }
    >
      <div className="flex items-start gap-2 min-w-0">
        <div className={cn('shrink-0 mt-0.5', isSnoozed || isArchived ? 'text-muted-foreground/40' : cfg.color)}>
          <Icon className="h-3.5 w-3.5" />
        </div>
        <div className="flex-1 min-w-0">
          {/* Title + age (comfortable matches Continuity index / inbox-folders teach) */}
          <div className={cn('flex items-baseline gap-2 min-w-0', !dense && 'mb-0.5')}>
            {thread.pinned && <Pin className="h-3 w-3 text-primary shrink-0 self-center" />}
            {isUnread && !thread.pinned && (
              <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-primary self-center" aria-hidden />
            )}
            <span
              className={cn(
                'text-sm truncate flex-1 text-foreground',
                (isUnread || active) && 'font-semibold',
              )}
            >
              {thread.title}
            </span>
            {thread.audioDuration && (
              <span className="text-xs px-1.5 py-0.5 rounded border border-border text-muted-foreground font-mono shrink-0 leading-none">
                {thread.audioDuration}
              </span>
            )}
            {thread.agentReplies != null && thread.agentReplies > 0 && (
              <span className="text-xs px-1.5 py-0.5 rounded bg-muted text-muted-foreground shrink-0 leading-none">
                {thread.agentReplies}
              </span>
            )}
            {!dense ? (
              <span className="shrink-0 text-[10px] tabular-nums text-muted-foreground/70">
                {thread.age}
              </span>
            ) : null}
          </div>

          {/* Snippet: plain preview (kind is on the icon; no "Flow ·" prefix) */}
          {thread.preview.trim() ? (
            <p className="truncate text-xs text-muted-foreground mt-0.5">
              {thread.preview.trim()}
            </p>
          ) : null}

          {/* Meta: workspace (+ age when dense) */}
          <div
            className={cn(
              'flex items-center gap-2 text-xs text-muted-foreground min-w-0',
              !dense && 'mt-0.5 text-[10px] text-muted-foreground/70',
            )}
          >
            {isSnoozed && thread.snoozedUntil ? (
              <span className="truncate flex items-center gap-1 min-w-0 text-amber-500/80">
                <Clock className="h-3 w-3 shrink-0" /> Wakes up {thread.snoozedUntil}
              </span>
            ) : dense ? (
              <span
                className="inline-flex items-center gap-1 truncate flex-1 min-w-0"
                title={workspace?.label ?? thread.source ?? 'Personal'}
              >
                {workspace ? (
                  <span
                    className="h-1.5 w-1.5 shrink-0 rounded-full"
                    style={{ backgroundColor: workspace.color }}
                    aria-hidden
                  />
                ) : null}
                <span className="truncate">
                  {workspace?.label ?? thread.source ?? 'Personal'}
                </span>
                {(thread.taggedOrganizationIds?.length ?? 0) > 1 ? (
                  <span className="tabular-nums shrink-0 text-muted-foreground/70">
                    +{(thread.taggedOrganizationIds!.length) - 1}
                  </span>
                ) : null}
              </span>
            ) : (
              <span className="truncate flex items-center gap-1 min-w-0 flex-1">
                <Clock className="h-2.5 w-2.5 shrink-0" />
                {workspace?.label ?? 'Personal'}
              </span>
            )}
            {thread.filed && <Folder className="h-3 w-3 shrink-0" />}
            {dense ? (
              <span className="ml-auto shrink-0 tabular-nums">{thread.age}</span>
            ) : workspace && (thread.taggedOrganizationIds?.length ?? 0) > 1 ? (
              <span className="tabular-nums shrink-0">
                +{(thread.taggedOrganizationIds!.length) - 1}
              </span>
            ) : null}
          </div>
        </div>
      </div>
    </SidebarListRow>
  )
}

// ---------------------------------------------------------------------------
// Props and component
// ---------------------------------------------------------------------------

interface InboxFeedProps {
  threads: Thread[]
  workspaces: InboxWorkspace[]
  activeId: string | null
  kindFilter: KindFilter
  activeExtension: string | null
  onSelect: (id: string, event?: MouseEvent) => void
  onFilterChange: (f: KindFilter) => void
  dense: boolean
  onDenseChange?: (dense: boolean) => void
  /** Sidebar: layout/density lives in panel menu; hide this feed's options menu. */
  hideLayoutMenu?: boolean
  searchOpen: boolean
  searchQuery: string
  onSearchOpen: () => void
  onSearchClose: () => void
  onSearchChange: (q: string) => void
  onArchive: (id: string) => void
  onSnooze: (id: string, label: string, hint: string) => void
  onUndo: () => void
  onRefetch: () => void
  onMarkAsUnread?: (id: string) => void
  onMarkAsRead?: (id: string) => void
  banner: { type: 'archive' | 'snooze'; label?: string; hint?: string } | null
  variant?: 'default' | 'sidebar'
  /** When set, enables "Mark all read" in the options menu */
  onMarkAllRead?: () => void
}

export function InboxFeed({
  threads,
  workspaces,
  activeId,
  kindFilter,
  activeExtension,
  onSelect,
  onFilterChange,
  dense,
  onDenseChange,
  searchOpen,
  searchQuery,
  onSearchOpen,
  onSearchClose,
  onSearchChange,
  onArchive,
  onSnooze,
  onUndo,
  onRefetch,
  onMarkAsUnread,
  onMarkAsRead,
  banner,
  variant = 'default',
  onMarkAllRead,
  hideLayoutMenu = false,
}: InboxFeedProps) {
  const searchRef = useRef<HTMLInputElement | null>(null) // kept for compat
  const { activeWorkspaceIds } = useWorkspace()
  const { needsPassword, loading: needsPasswordLoading, dismiss: dismissPasswordPrompt } = useNeedsPassword()

  const isSidebar = variant === 'sidebar'

  const visible = threads.filter(t => {
    if (kindFilter === 'archived') return t.status === 'archived'
    if (!isInboxThreadVisible(t, activeWorkspaceIds)) return false
    if (kindFilter === 'snoozed') return t.status === 'snoozed'
    if (t.status === 'archived' || t.status === 'snoozed') return false
    if (kindFilter !== 'all' && t.kind !== kindFilter) return false
    if (kindFilter === 'extension' && activeExtension && t.extensionId !== activeExtension) return false
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase()
      return t.title.toLowerCase().includes(q) || t.preview.toLowerCase().includes(q) || t.source.toLowerCase().includes(q)
    }
    return true
  })

  const totalUnread = threads.filter(t => isInboxThreadVisible(t, activeWorkspaceIds) && t.status === 'unread').length

  return (
    <div
      data-testid={testIds.inbox.feed}
      className={cn(
        'flex flex-col overflow-hidden min-h-0',
        isSidebar ? 'flex-1 w-full min-w-0' : 'shrink-0 border-r border-border',
      )}
      style={isSidebar ? undefined : { width: 300 }}
    >
      {/* Header row: title varies by variant; sidebar leaves room for parent panel "Inbox" label */}
      <div
        className={cn(
          'flex items-center gap-2 border-b border-border shrink-0',
          isSidebar ? 'px-2 h-9' : 'px-3 h-10',
        )}
      >
        {!isSidebar ? (
          <span className="font-semibold text-sm flex-1 text-foreground truncate">
            {kindFilter === 'archived' ? 'Archive' : kindFilter === 'snoozed' ? 'Snoozed' : 'Inbox'}
          </span>
        ) : (
          <span className="text-[11px] text-muted-foreground flex-1 min-w-0 truncate">
            {kindFilter === 'archived'
              ? 'Archive view'
              : kindFilter === 'snoozed'
                ? 'Snoozed view'
                : kindFilter === 'all'
                  ? ''
                  : kindFilter === 'extension'
                    ? 'Extensions'
                    : `${String(kindFilter).charAt(0).toUpperCase()}${String(kindFilter).slice(1)}`}
          </span>
        )}
        {!(isSidebar && hideLayoutMenu) ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <IconButton icon={<MoreVertical className="h-4 w-4" />} label="Inbox options" variant="ghost" size="sm" />
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              className={QUIET_OVERFLOW_MENU_CONTENT_CLASS}
              collisionPadding={QUIET_OVERFLOW_MENU_COLLISION_PADDING}
            >
              {onDenseChange ? (
                <>
                  <DropdownMenuLabel className="text-xs font-medium text-muted-foreground">Layout</DropdownMenuLabel>
                  <DropdownMenuItem onClick={() => onDenseChange(false)} className={cn(!dense && 'text-primary')}>
                    <AlignJustify className="mr-2 h-4 w-4" /> Detailed
                    {!dense && <span className="ml-auto text-xs opacity-50">on</span>}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => onDenseChange(true)} className={cn(dense && 'text-primary')}>
                    <List className="mr-2 h-4 w-4" /> Dense
                    {dense && <span className="ml-auto text-xs opacity-50">on</span>}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                </>
              ) : null}
              <DropdownMenuItem onClick={() => { /* search always visible */ }}>
                <Search className="mr-2 h-4 w-4" /> Search inbox
                <span className="ml-auto text-xs text-muted-foreground">Cmd+F</span>
              </DropdownMenuItem>
              <DropdownMenuItem onClick={onRefetch}>
                <RefreshCw className="mr-2 h-4 w-4" /> Refresh
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => onMarkAllRead?.()} disabled={!onMarkAllRead}>
                <CheckCheck className="mr-2 h-4 w-4" /> Mark all read
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ) : null}
      </div>

      {/* Kind filter tabs (workspace scope: sidebar panel menu) */}
      {!needsPasswordLoading && needsPassword && (
        <InboxPasswordSetupBanner onDismiss={dismissPasswordPrompt} />
      )}

      <div className="px-2 py-1.5 border-b border-border shrink-0 min-w-0">
        <KindTabs
          filter={kindFilter}
          onChange={onFilterChange}
          threads={threads}
          activeWorkspaceIds={activeWorkspaceIds}
        />
      </div>

      {/* Banners */}
      {banner && (
        banner.type === 'snooze'
          ? <SnoozeBanner label={banner.label ?? ''} hint={banner.hint ?? ''} onUndo={onUndo} />
          : <ArchiveBanner onUndo={onUndo} />
      )}

      {/* Search bar - always visible in sidebar */}
      <SidebarFilterBar
        searchValue={searchQuery}
        onSearchChange={onSearchChange}
        searchPlaceholder="Filter threads..."
      />

      {/* Thread list */}
      {kindFilter === 'archived' && visible.length === 0 ? (
        <SidebarEmptyState
          icon={<Archive className="h-8 w-8" />}
          title="Archive is empty"
          description="Archived threads live here, not deleted."
        />
      ) : (
        <div className="flex-1 overflow-y-auto min-h-0">
          {kindFilter === 'archived' && visible.length > 0 && (
            <div className="px-3 py-2 border-b border-border bg-muted/20">
              <p className="text-[10px] text-muted-foreground/60">
                {visible.length} archived thread{visible.length !== 1 ? 's' : ''} - not deleted, fully searchable
              </p>
            </div>
          )}
          {visible.length === 0 && kindFilter !== 'archived' && (
            <div className="px-4 py-8 text-center text-sm text-muted-foreground">No threads in this view.</div>
          )}
          {visible.map(t => (
            <ThreadRow
              key={t.id}
              thread={t}
              active={t.id === activeId}
              dense={dense}
              onClick={(event) => onSelect(t.id, event)}
              onArchive={onArchive}
              onSnooze={onSnooze}
              onMarkAsUnread={onMarkAsUnread}
              onMarkAsRead={onMarkAsRead}
              workspace={resolveThreadWorkspace(t, workspaces)}
            />
          ))}
        </div>
      )}

      {/* Status bar */}
      <div className="flex items-center gap-2 px-3 py-1.5 border-t border-border bg-background shrink-0">
        {!isSidebar && (
          <>
            <span className="text-[10px] text-muted-foreground/50">E archive</span>
            <span className="text-[10px] text-muted-foreground/50">H snooze</span>
          </>
        )}
        <div className="flex-1" />
        <span className="text-[10px] text-muted-foreground/40 tabular-nums">
          {visible.length} / {totalUnread} unread
        </span>
      </div>
    </div>
  )
}

export { ThreadRow as InboxThreadRow }
